import React, { useEffect, useState, useCallback } from 'react';
import { MessageCircle, Send, Loader2, Heart, CornerDownRight, User, BadgeCheck } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `${diffD}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};

const CommentNode = ({ comment, replies, onReply, onLike, depth = 0 }) => {
  const [showReply, setShowReply] = useState(false);
  const isVerified = !!comment.verified;
  return (
    <div
      className={`${depth > 0 ? 'ml-6 pl-4 border-l-2 border-steel-700/40' : ''} mb-4`}
      data-testid={`comment-${comment.id}`}
    >
      <div
        className={`bg-gunmetal-800/70 rounded-lg p-4 ${
          isVerified
            ? 'border border-amber-400/50 shadow-lg shadow-amber-400/10'
            : 'border border-steel-700/40'
        }`}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                isVerified
                  ? 'bg-gradient-to-br from-amber-400 to-orange-500'
                  : 'bg-gradient-to-br from-accent-red to-amber-500'
              }`}
            >
              {(comment.author || '?').charAt(0).toUpperCase()}
            </div>
            <span className="text-steel-100 font-bold text-sm">{comment.author}</span>
            {isVerified && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-400/15 border border-amber-400/40 rounded-full text-[9px] tracking-[0.2em] uppercase font-bold text-amber-300"
                data-testid={`verified-${comment.id}`}
                title="Verified MINDESIGN voice"
              >
                <BadgeCheck size={10} className="text-amber-400" />
                Verified · MINDESIGN
              </span>
            )}
            <span className="text-steel-600 text-xs">·</span>
            <span className="text-steel-500 text-xs">{formatTime(comment.created_at)}</span>
          </div>
        </div>
        <p
          className={`leading-relaxed whitespace-pre-wrap text-sm ${
            isVerified ? 'text-amber-50' : 'text-steel-200'
          }`}
        >
          {comment.body}
        </p>
        <div className="flex items-center gap-4 mt-3 text-xs">
          <button
            type="button"
            onClick={() => onLike(comment.id)}
            className="flex items-center gap-1 text-steel-500 hover:text-accent-red transition-colors"
            data-testid={`like-${comment.id}`}
          >
            <Heart size={12} />
            <span>{comment.likes || 0}</span>
          </button>
          <button
            type="button"
            onClick={() => setShowReply(!showReply)}
            className="flex items-center gap-1 text-steel-500 hover:text-amber-400 transition-colors"
            data-testid={`reply-toggle-${comment.id}`}
          >
            <CornerDownRight size={12} />
            Reply
          </button>
        </div>
      </div>

      {showReply && (
        <ReplyForm
          parentId={comment.id}
          onSubmit={async (data) => {
            await onReply(data);
            setShowReply(false);
          }}
        />
      )}

      {replies.map((r) => (
        <CommentNode
          key={r.id}
          comment={r}
          replies={[]} // single-level threading; flatten deeper replies
          onReply={onReply}
          onLike={onLike}
          depth={depth + 1}
        />
      ))}
    </div>
  );
};

const ReplyForm = ({ parentId, onSubmit, autoFocus = true }) => {
  const [author, setAuthor] = useState(
    typeof window !== 'undefined'
      ? sessionStorage.getItem('mindesign_blog_author') || ''
      : ''
  );
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!author.trim() || !body.trim()) {
      toast.error('Name and a message are required.');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({ author: author.trim(), body: body.trim(), parent_id: parentId });
      sessionStorage.setItem('mindesign_blog_author', author.trim());
      setBody('');
    } catch (e) {
      toast.error(e.message || 'Could not post comment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className={`${parentId ? 'ml-6 mt-2 pl-4 border-l-2 border-amber-400/30' : ''} bg-gunmetal-900/50 rounded-lg p-3 mb-3`}
      data-testid={parentId ? `reply-form-${parentId}` : 'comment-form'}
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
        <Input
          placeholder="Your name"
          value={author}
          onChange={(e) => setAuthor(e.target.value)}
          className="md:col-span-1 bg-gunmetal-800 border-steel-700 text-steel-100 placeholder:text-steel-500 h-9 text-sm"
          required
          data-testid={parentId ? `reply-name-${parentId}` : 'comment-name'}
        />
      </div>
      <Textarea
        placeholder={parentId ? 'Reply with grit and respect…' : 'Add to the debate. Keep it real.'}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={parentId ? 2 : 3}
        className="bg-gunmetal-800 border-steel-700 text-steel-100 placeholder:text-steel-500 text-sm mb-2"
        required
        autoFocus={autoFocus}
        data-testid={parentId ? `reply-body-${parentId}` : 'comment-body'}
      />
      <div className="flex justify-end">
        <Button
          type="submit"
          disabled={submitting}
          size="sm"
          className="bg-accent-red hover:bg-accent-red-dark text-white text-xs font-bold tracking-wider uppercase"
          data-testid={parentId ? `reply-submit-${parentId}` : 'comment-submit'}
        >
          {submitting ? (
            <><Loader2 className="mr-1.5 animate-spin" size={12} /> Posting…</>
          ) : (
            <><Send className="mr-1.5" size={12} /> {parentId ? 'Reply' : 'Post Comment'}</>
          )}
        </Button>
      </div>
    </form>
  );
};

export const BlogComments = ({ postId }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/blog/posts/${postId}/comments`);
      const data = await res.json();
      setComments(data.comments || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [postId]);

  useEffect(() => {
    if (postId) load();
  }, [postId, load]);

  const submitComment = async ({ author, body, parent_id }) => {
    const adminToken =
      typeof window !== 'undefined'
        ? localStorage.getItem('mindesign_admin_token')
        : null;
    const headers = { 'Content-Type': 'application/json' };
    if (adminToken) headers['X-Admin-Token'] = adminToken;
    const res = await fetch(`${API_URL}/api/blog/posts/${postId}/comments`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ author, body, parent_id: parent_id || null }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Could not post.');
    }
    const data = await res.json();
    if (data?.comment?.verified) {
      toast.success('Comment posted as Verified · MINDESIGN ✓');
    } else {
      toast.success('Comment posted 🔥');
    }
    await load();
  };

  const likeComment = async (commentId) => {
    try {
      await fetch(`${API_URL}/api/blog/comments/${commentId}/like`, { method: 'POST' });
      // optimistic refresh
      setComments((cs) =>
        cs.map((c) =>
          c.id === commentId ? { ...c, likes: (c.likes || 0) + 1 } : c
        )
      );
    } catch {
      // silent
    }
  };

  // Build threaded structure: top-level + replies grouped by parent_id
  const topLevel = comments.filter((c) => !c.parent_id);
  const repliesByParent = comments.reduce((acc, c) => {
    if (c.parent_id) {
      acc[c.parent_id] = acc[c.parent_id] || [];
      acc[c.parent_id].push(c);
    }
    return acc;
  }, {});

  return (
    <div className="mt-12 pt-8 border-t border-steel-700/40" data-testid="blog-comments">
      <div className="flex items-center gap-2 mb-5">
        <MessageCircle className="text-accent-red" size={22} />
        <h3 className="text-2xl font-black text-steel-100 tracking-wide">
          THE DEBATE — {comments.length} {comments.length === 1 ? 'voice' : 'voices'}
        </h3>
      </div>

      <ReplyForm parentId={null} onSubmit={submitComment} autoFocus={false} />

      {loading ? (
        <div className="flex items-center gap-2 text-steel-500 py-4">
          <Loader2 className="animate-spin" size={16} /> Loading the conversation…
        </div>
      ) : topLevel.length === 0 ? (
        <div className="text-center py-8 text-steel-500" data-testid="comments-empty">
          <User className="mx-auto mb-2 opacity-50" size={32} />
          No comments yet. Be the first to drop a real take.
        </div>
      ) : (
        <div>
          {topLevel.map((c) => (
            <CommentNode
              key={c.id}
              comment={c}
              replies={repliesByParent[c.id] || []}
              onReply={submitComment}
              onLike={likeComment}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default BlogComments;
